import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";

// 동의 항목 필수 여부
export interface IAgreementRequired {
  terms: boolean;
  privacyPolicy: boolean;
  marketing: boolean;

  over14: boolean;
  thirdPartyProvision: boolean;
}
export const agreementRequired: IAgreementRequired = {
  terms: true,
  privacyPolicy: true,
  marketing: false,
  over14: true,
  thirdPartyProvision: true,
};

// 가입 경로
type TSignUpSource =
  | "지인 추천"
  | "앱스토어/플레이스토어 검색"
  | "네이버 검색"
  | "구글 검색"
  | "블로그"
  | "카페"
  | "유튜브"
  | "인스타그램"
  | "페이스북";
export const signUpSources: TSignUpSource[] = [
  "지인 추천",
  "앱스토어/플레이스토어 검색",
  "네이버 검색",
  "구글 검색",
  "블로그",
  "카페",
  "유튜브",
  "인스타그램",
  "페이스북",
];

// 관심 항목
type TInterestedItems =
  | "웨딩홀"
  | "스드메"
  | "신혼여행"
  | "예복"
  | "한복"
  | "예물"
  | "예단"
  | "가전"
  | "가구"
  | "혼수";
export const interestedItemsPrev: TInterestedItems[] = [
  "웨딩홀",
  "스드메",
  "신혼여행",
  "예복",
  "한복",
  "예물",
  "예단",
  "가전",
  "가구",
];
// 이전 관심 품목으로 바꿔야되면 interestedItemsPrev로 바꿔주세요
export const interestedItems: TInterestedItems[] = ["웨딩홀", "스드메", "혼수", "신혼여행"];

// 개인정보
interface IPersonalInfo {
  [key: string]: any;
  loginId: null | string;
  loginIdVerified: null | boolean;
  password: null | string;
  confirmPassword: null | string;
  role: null | "2" | "1"; // 2 male 1 female
  name: null | string;
  tel: null | string;
  telVerified: null | boolean;
  nick: null | string;
}

// 예식 정보
interface IWeddingInfo {
  [key: string]: any;
  mateName: null | string;
  mateTel: null | string;
  weddingTsp: string | null;
  weddingTspType: number;
  region: null | string;
  weddinghallName: string | null;
}

// 추가정보
interface IExtralInfo {
  [key: string]: any;
  interestItems: null | TInterestedItems[];
  hasPlanner: null | boolean;
  weddingbookPlanner: null | boolean;
  inPathId: null | TSignUpSource;
  invitationCode: null | string;
}

// 회원가입 데이터
export interface ISignUpData {
  [key: string]: any;
  personalInfo: IPersonalInfo;
  agreement: {
    [K in keyof IAgreementRequired]: boolean;
  };
  weddingInfo: IWeddingInfo;
  extraInfo: IExtralInfo;
  isComplete?: null | "basic" | "additional";
}

// 회원가입 상태
export interface ISignUpState {
  data: ISignUpData;
}

// 초기 상태
const initialState = {
  data: {
    personalInfo: {
      loginId: null,
      loginIdVerified: null,
      password: null,
      confirmPassword: null,
      role: null,
      name: null,
      tel: null,
      telVerified: null,
      nick: null,
    },
    agreement: {
      terms: false,
      privacyPolicy: false,
      marketing: false,
      over14: false,
      thirdPartyProvision: false,
    },
    weddingInfo: {
      mateName: null,
      mateTel: null,
      weddingTsp: null,
      weddingTspType: null,
      region: null,
      weddinghallName: null,
    },
    extraInfo: {
      interestItems: null,
      hasPlanner: null,
      weddingbookPlanner: null,
      inPathId: null,
      invitationCode: null,
    },
    isComplete: null,
  },
} as ISignUpState;

const signUpSlice = createSlice({
  name: "signUp",
  initialState,
  reducers: {
    setResetData(state) {
      state.data = initialState.data;
    },
    setData(state, action: PayloadAction<{ data: any }>) {
      for (let key in action.payload.data) {
        if (state.data.hasOwnProperty(key)) {
          const newData = { ...state.data[key], ...action.payload.data[key] };
          state.data[key] = newData;
        }
      }
    },
    setPersonalInfo(state, action: PayloadAction<{ data: any }>) {
      state.data.personalInfo = { ...state.data.personalInfo, ...action.payload.data };
    },
    setWeddingInfo(state, action: PayloadAction<{ data: any }>) {
      state.data.weddingInfo = { ...state.data.weddingInfo, ...action.payload.data };
    },
    setExtraInfo(state, action: PayloadAction<{ data: any }>) {
      state.data.extraInfo = { ...state.data.extraInfo, ...action.payload.data };
    },
    setAgreementInfo(state, action: PayloadAction<{ data: any }>) {
      state.data.agreement = { ...state.data.agreement, ...action.payload.data };
    },
    setIsComplete(state, action: PayloadAction<{ state: "basic" | "additional" }>) {
      state.data.isComplete = action.payload.state;
    },
  },
});

export const {
  setData,
  setResetData,
  setPersonalInfo,
  setExtraInfo,
  setWeddingInfo,
  setAgreementInfo,
  setIsComplete,
} = signUpSlice.actions;
export const SignUp = (state: RootState) => state.signUp;
export const SignUpData = (state: RootState) => state.signUp.data;
export const SignUpIsComplete = (state: RootState) => state.signUp.data.isComplete;
export default signUpSlice.reducer;
